import { memo, useMemo, useCallback } from "react";
import { Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent from "components/table/TableRowComponent";
import { HEADER_WHEEL_LIST } from "constants/data";
import EditableTextCell from "components/table/EditableTextCell";
import EditableNumberCell from "components/table/EditableNumberCell";
import {
  TUploadImageType,
  WheelBrandDetailUpdateRequest,
  WheelBrandListItem,
} from "../types/wheel-brand";
import useWheelBrandHandlers from "../hooks/useWheelBrandHandlers";
import ActionButtonsCell from "./ActionButtonsCell";

interface TableWheelBrandProps {
  wheelBrands: WheelBrandListItem[];
  openModal: (type: TUploadImageType, item: WheelBrandListItem) => void;
  onUpdate: (
    brandCd: string,
    key: keyof WheelBrandDetailUpdateRequest,
    data: string | number,
  ) => void;
}

/**
 * @param {TableWheelBrandProps} props - The component props
 */
const TableWheelBrand = memo<TableWheelBrandProps>(({ wheelBrands, openModal, onUpdate }) => {
  // Create a wrapper function to match the generic hook signature
  const handleUpdate = useCallback(
    (
      identifier: string | number,
      key: keyof WheelBrandDetailUpdateRequest,
      data: string | number,
    ) => {
      // Since our onUpdate only accepts string brandCd, we need to ensure identifier is string
      if (typeof identifier === "string") {
        onUpdate(identifier, key, data);
      }
    },
    [onUpdate],
  );

  const {
    handleTextBlur,
    handleNumberBlur,
    handleTextInput,
    handleNumberInput,
    handleNumberKeyDown,
    handleFocus,
    getError,
    parseNumber,
  } = useWheelBrandHandlers<WheelBrandDetailUpdateRequest>({ onUpdate: handleUpdate });

  // Memoize table container styles to prevent unnecessary re-renders
  const tableContainerStyles = useMemo(
    () => ({
      boxShadow: "none",
      width: "100%",
      minWidth: "1400px",
      borderCollapse: "unset" as const,
      borderSpacing: "2px 1px",
    }),
    [],
  );

  // Memoize header cell styles
  const brandCdCellStyles = useMemo(
    () => ({
      backgroundColor: "var(--offwhite-400)",
      border: "2px solid var(--silver-150)",
    }),
    [],
  );

  return (
    <TableContainer component={Table} sx={tableContainerStyles}>
      <TableHead>
        <TableRowComponent data={HEADER_WHEEL_LIST} type="header" />
      </TableHead>
      <TableBody>
        {wheelBrands.map((brand, index) => (
          <TableRow key={brand.brand_cd}>
            {/* Brand Code Cell - Read Only */}
            <TableCellComponent sx={brandCdCellStyles}>{brand.brand_cd}</TableCellComponent>

            {/* Maker Name Cell - Editable Text */}
            <EditableTextCell
              value={brand.maker_name}
              field="maker_name"
              identifier={brand.brand_cd}
              index={index}
              maxWidth={HEADER_WHEEL_LIST[1].maxWidth || "223px"}
              isEditable={brand.titleEditable === "true"}
              hasError={getError("maker_name", index)}
              onBlur={handleTextBlur}
              onInput={handleTextInput}
              onFocus={handleFocus}
            />

            {/* Brand Name Cell - Editable Text */}
            <EditableTextCell
              value={brand.brand_name}
              field="brand_name"
              identifier={brand.brand_cd}
              index={index}
              maxWidth={HEADER_WHEEL_LIST[2].maxWidth || "320px"}
              isEditable={brand.titleEditable === "true"}
              hasError={getError("brand_name", index)}
              onBlur={handleTextBlur}
              onInput={handleTextInput}
              onFocus={handleFocus}
            />

            {/* Fixed Discount Cell - Editable Number */}
            <EditableNumberCell
              value={brand.fixed_discount ? String(brand.fixed_discount) : ""}
              field="fixed_discount"
              identifier={brand.brand_cd}
              index={index}
              maxWidth={HEADER_WHEEL_LIST[3].maxWidth || "130px"}
              color={brand.fixed_discount_color}
              hasError={getError("fixed_discount", index)}
              onBlur={handleNumberBlur}
              onInput={handleNumberInput}
              onKeyDown={handleNumberKeyDown}
              onFocus={handleFocus}
              parseNumber={parseNumber}
            />

            {/* Sale Price Rate Cost Cell - Editable Number */}
            <EditableNumberCell
              value={brand.sale_price_rate_cost ? String(brand.sale_price_rate_cost) : ""}
              field="sale_price_rate_cost"
              identifier={brand.brand_cd}
              index={index}
              maxWidth={HEADER_WHEEL_LIST[4].maxWidth || "130px"}
              color={brand.sale_price_rate_cost_color}
              hasError={getError("sale_price_rate_cost", index)}
              onBlur={handleNumberBlur}
              onInput={handleNumberInput}
              onKeyDown={handleNumberKeyDown}
              onFocus={handleFocus}
              parseNumber={parseNumber}
            />

            {/* Action Buttons Cell */}
            <ActionButtonsCell item={brand} handleSelectedWheel={openModal} />
          </TableRow>
        ))}
      </TableBody>
    </TableContainer>
  );
});

TableWheelBrand.displayName = "TableWheelBrand";

export default TableWheelBrand;
